// Operator-aware dobór szablonów w modalach zgód / regulacji / wniosków urzędowych.
// MIĘKKO: sortujemy i oznaczamy wg operatora (OSD) projektu, ale NIGDY nie ukrywamy —
// TAURON-owy projekt nie może stracić szablonu ENERGA, dopóki nie ma wariantu TAURON/
// wspólnego. Odpowiednik backendowego doc_templates.selection (exact > 'inny' > reszta).
import type { DocTemplate } from "./types";

export const SHARED_OPERATOR = "inny";

export const OPERATOR_SHORT_LABELS: Record<string, string> = {
  energa: "ENERGA",
  tauron: "TAURON",
  inny: "uniwersalny",
};

export function operatorShort(operator: string): string {
  return OPERATOR_SHORT_LABELS[operator] ?? operator;
}

// Regulacja prawna w Wydziale OMI („Wniosek o regulację - TD Oddział Gliwice",
// PSP zadania, załączniki, opisy urządzeń) to procedura TAURON-a. W projektach
// ENERGA/„inny" ta ścieżka nie istnieje, więc jej UI się nie pokazuje. W przeciwieństwie
// do doboru szablonów (który tylko sortuje) TU chowamy — ale wywołania zawsze dopuszczają
// wyjątek „dane już są": projekt przepięty między operatorami nie może zgubić treści.
export const REGULACJA_OPERATOR = "tauron";

export function isRegulacjaOperator(operator: string | null | undefined): boolean {
  return operator === REGULACJA_OPERATOR;
}

export interface OperatorTemplateOption {
  template: DocTemplate;
  exact: boolean; // wariant dokładnie tego operatora
  shared: boolean; // wariant wspólny ('inny')
  // Krótki dopisek do etykiety opcji: "· TAURON", "· uniwersalny" albo
  // "· ENERGA (inny operator)" gdy tylko fallback.
  badge: string;
}

function rank(templateOperator: string, operator: string): number {
  if (templateOperator === operator) return 0;
  if (templateOperator === SHARED_OPERATOR) return 1;
  return 2;
}

/** Dopisek do etykiety opcji: „· TAURON", „· uniwersalny", „· ENERGA (inny operator)". */
export function operatorOptionBadge(templateOperator: string, operator: string): string {
  if (templateOperator === operator) return `· ${operatorShort(operator)}`;
  if (templateOperator === SHARED_OPERATOR) return "· uniwersalny";
  return `· ${operatorShort(templateOperator)} (inny operator)`;
}

/**
 * Ta sama reguła trafności dla dowolnej listy z polem `operator` (szablony ODT,
 * szablony formatek DXF): dokładny operator > wspólny > reszta. Sortowanie jest
 * stabilne, więc kolejność wejściowa (u nas: firmowe, potem najnowsze) zostaje
 * zachowana w obrębie warstwy. NIGDY nie filtrujemy — brak wariantu pod operatora
 * nie może zabrać projektantowi szablonu.
 */
export function sortByOperator<T extends { operator: string }>(items: T[], operator: string): T[] {
  return [...items].sort((a, b) => rank(a.operator, operator) - rank(b.operator, operator));
}

/** Szablony `allowedKinds` posortowane wg trafności pod operatora projektu, z metadanymi. */
export function pickTemplatesForOperator(
  templates: DocTemplate[],
  allowedKinds: string[],
  operator: string,
): OperatorTemplateOption[] {
  return templates
    .filter((t) => allowedKinds.includes(t.kind))
    .map((t) => {
      const exact = t.operator === operator;
      const shared = t.operator === SHARED_OPERATOR;
      const badge = exact
        ? `· ${operatorShort(operator)}`
        : shared
          ? "· uniwersalny"
          : `· ${operatorShort(t.operator)} (inny operator)`;
      return { template: t, exact, shared, badge };
    })
    .sort((a, b) => rank(a.template.operator, operator) - rank(b.template.operator, operator));
}
